import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  TypedMutationOnQueryStarted
} from '@reduxjs/toolkit/query';
import { ErrorApiResponse, ID } from '@ianesthesie-kit/services';
import {
  DoctorHospital,
  MedicalCredential,
  MinimalDoctorDto,
  UserAppSettings,
  UserConfig
} from './doctorBff.model';
import { DoctorBffService } from './doctorBff.service';
import { wrapError } from './error';
import { ApiLanguage } from '@ianesthesie-kit/config';
import { SentryService } from '@ianesthesie-kit/sentry';
import getOr from 'lodash/fp/getOr';
import type { RootState, UseQueryResult } from '@/store';

export const RTK_QUERY_TAGS = [
  'Allergy',
  'Intolerance',
  'ConsultationStat',
  'CustomDocuments',
  'MedicalHo',
  'MedicalDocument',
  'BloodManagement',
  'PhysicalExamination',
  'Meds',
  'LabTest',
  'Intubation',
  'SurgeryInfo',
  'AnesthesiaInfo',
  'AnesthesiaRisk',
  'PregnancyInfo',
  'Notification',
  'PregnancyInfo',
  'ActiveSurgicalEvent',
  'Dashboard',
  'SearchSurgicalEvents',
  'Protocol',
  'ProtocolStatuses',
  'PendingPatients',
  'UserConfig',
  'PostopInfo',
  'PostopSurgeryInfo',
  'DoctorHospitals',
  'DocumentTemplates',
  'Doctor',
  'SurgeonName',
  'Terra',
  'Scores',
  'Invoice',
  'EpicSurgicalEvent',
  'EpicDocuments',
  'DismissedNlpSuggestions',
  'UploadQrExists',
  'AutomationActionExecutionLogs',
  'PainPostopInfo',
  'HospitalSpecificData',
  'LifenInfo',
  'LifenDocumentsTrackingInfo',
  'LifenAvailableDocumentTypes',
  'PatientsAppInfo',
  'Hospital',
  'PreviousConsultations',
  'MhoSuggestions',
  'Premedication',
  'PremedicationProtocol',
  'BillingLines',
  'BillingLineEvents',
  'BillingPayments',
  'BillingDashboard',
  'BillingSummary',
  'BillingSettings',
  'BillingEntities',
  'BillingEntityMembers',
  'BillingTerminalReaders',
  'DeskPayments',
  'FollowUpPlans',
  'Questionnaires'
] as const;

export type InvalidateArr = (
  | (typeof RTK_QUERY_TAGS)[number]
  | { [k: string]: string | undefined; type: (typeof RTK_QUERY_TAGS)[number] }
)[];

export const doctorBffApi = createApi({
  reducerPath: 'doctorBffApi',
  baseQuery: fakeBaseQuery<ErrorApiResponse>(),
  tagTypes: RTK_QUERY_TAGS,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: 15,
  endpoints: (builder) => ({
    medicalCredentials: builder.query<MedicalCredential[], void>({
      queryFn: () =>
        DoctorBffService.fetchCredentials()
          .then((resp) => ({
            data: resp.data
          }))
          .catch(wrapError)
    }),
    doctors: builder.query<MinimalDoctorDto[], void>({
      keepUnusedDataFor: 120,
      providesTags: ['Doctor'],
      queryFn: () =>
        DoctorBffService.fetchDoctorsOfCurrentHospital()
          .then((hosp) => ({
            data: hosp
          }))
          .catch(wrapError)
    }),

    doctorById: builder.query<MinimalDoctorDto, ID>({
      keepUnusedDataFor: 120,
      providesTags: ['Doctor'],
      queryFn: (doctorId) =>
        DoctorBffService.fetchDoctorOfCurrentHospitalById(doctorId)
          .then((hosp) => ({
            data: hosp
          }))
          .catch(wrapError)
    }),

    doctorHospitals: builder.query<DoctorHospital[], void>({
      keepUnusedDataFor: Infinity,
      providesTags: ['DoctorHospitals'],
      queryFn: () =>
        DoctorBffService.fetchDoctorHospitals()
          .then((hosp) => ({
            data: hosp
          }))
          .catch(wrapError)
    }),

    setActiveDoctorHospital: builder.mutation<boolean, DoctorHospital | ID>({
      invalidatesTags: ['DoctorHospitals', 'Doctor', 'HospitalSpecificData'],
      queryFn: (param) =>
        DoctorBffService.setActiveDoctorHospital(param)
          .then((tokenUpdated) => ({
            data: tokenUpdated
          }))
          .catch(wrapError)
    }),

    userConfig: builder.query<UserConfig, void>({
      providesTags: ['UserConfig'],
      keepUnusedDataFor: Infinity,
      queryFn: () =>
        DoctorBffService.getUserConfig()
          .then((conf) => ({
            data: conf
          }))
          .catch(wrapError)
    }),

    updateUserLanguage: builder.mutation<UserConfig, ApiLanguage>({
      invalidatesTags: ['UserConfig'],
      queryFn: (lang) =>
        DoctorBffService.updateUserLanguage(lang)
          .then((conf) => ({
            data: conf
          }))
          .catch(wrapError)
    }),

    updateUserAppSettings: builder.mutation<
      UserConfig,
      Partial<UserAppSettings> | undefined
    >({
      queryFn: (settings, { getState }) =>
        mergeWithCurrent(settings, getState())
          .then((updatedSettings) =>
            DoctorBffService.updateUserAppSettings(updatedSettings)
          )
          .then((conf) => ({
            data: conf
          }))
          .catch(wrapError),
      async onQueryStarted(settingsUpdates, { dispatch, queryFulfilled }) {
        let updated = false;
        dispatch(
          doctorBffApi.util.updateQueryData(
            'userConfig',
            undefined,
            (draft) => {
              if (draft) {
                updated = true;
                draft.settings = {
                  ...draft.settings,
                  ...settingsUpdates
                };
              }
            }
          )
        );
        try {
          await queryFulfilled;
          if (!updated) {
            dispatch(doctorBffApi.util.invalidateTags(['UserConfig']));
          }
        } catch (err) {
          SentryService.getInstance().captureException(err);
        }
      }
    })
  })
});

export const {
  useMedicalCredentialsQuery: useMedicalCredentials,
  useDoctorsQuery: useCurrentHospitalDoctors,
  useDoctorByIdQuery: useCurrentHospitalDoctor,
  useDoctorHospitalsQuery,
  useSetActiveDoctorHospitalMutation,
  useUserConfigQuery,
  useUpdateUserLanguageMutation,
  useUpdateUserAppSettingsMutation
} = doctorBffApi;

const selectIsAdmin = (r: any) => ({
  ...r,
  data: r.data?.find((dh: DoctorHospital) => dh.active)?.hospitalAdmin ?? false
});

export const useIsHospitalAdmin = () =>
  useDoctorHospitalsQuery(undefined, {
    selectFromResult: selectIsAdmin
  });

export const invalidateAllBffApiTags = () =>
  doctorBffApi.util?.invalidateTags(Array.from(RTK_QUERY_TAGS));

/** The part of a doctor-bff mutation's `onQueryStarted` api that tag invalidation needs. */
type TagInvalidationApi = Pick<
  Parameters<
    NonNullable<
      TypedMutationOnQueryStarted<
        unknown,
        unknown,
        BaseQueryFn,
        typeof doctorBffApi.reducerPath
      >
    >
  >[1],
  'getState' | 'dispatch'
>;

/**
 * Invalidates every cached query whose tag id contains the patient id or the surgical event id,
 * plus the dashboards. Used after a mutation that gives the patient a new active surgical event.
 */
export const invalidatePatientScopedTags = (
  api: TagInvalidationApi,
  patientId: ID,
  seId: ID
) => {
  const provided = api.getState().doctorBffApi.provided;
  if (typeof provided !== 'object') {
    return;
  }

  const arr: InvalidateArr = Object.entries(provided.tags)
    .map(([tagType, cacheKeys]) =>
      Object.keys(cacheKeys).map((k) => ({
        type: tagType as (typeof RTK_QUERY_TAGS)[number],
        id: k
      }))
    )
    .flat()
    .filter(
      (cacheKey) =>
        cacheKey.id.includes(patientId) || cacheKey.id.includes(seId)
    );
  arr.push('Dashboard');
  api.dispatch(doctorBffApi.util.invalidateTags(arr));
};

export function useUserAppSetting<K extends keyof UserAppSettings>(
  key: K
): UserAppSettings[K] | undefined;
export function useUserAppSetting<K extends keyof UserAppSettings>(
  key: K,
  defaultValue: NonNullable<UserAppSettings[K]>
): NonNullable<UserAppSettings[K]>;
export function useUserAppSetting<K extends keyof UserAppSettings>(
  key: K,
  defaultValue?: UserAppSettings[K]
): UserAppSettings[K] | undefined;
export function useUserAppSetting<K extends keyof UserAppSettings>(
  key: K,
  defaultValue?: UserAppSettings[K],
  fullResult?: boolean
): UseQueryResult<UserAppSettings[K], any>;
export function useUserAppSetting<K extends keyof UserAppSettings>(
  key: K,
  defaultValue?: UserAppSettings[K],
  fullResult?: boolean
): UserAppSettings[K] | UseQueryResult<UserAppSettings[K], any> | undefined {
  const { data: userConfig, ...res } = useUserConfigQuery();

  if (!fullResult && (!key || !userConfig?.settings)) {
    return defaultValue;
  }

  const value =
    (getOr(defaultValue, key, userConfig?.settings) as any) ?? defaultValue;
  if (fullResult) {
    return {
      ...res,
      data: value,
      currentData: value
    };
  }

  return value;
}

function mergeWithCurrent(
  newSettings: Partial<UserAppSettings> | undefined,
  state: unknown
): Promise<UserAppSettings> {
  return new Promise((resolve) => {
    const existingSettings = doctorBffApi.endpoints?.userConfig.select()(
      state as RootState
    ).data?.settings;
    resolve({
      ...existingSettings,
      ...newSettings
    });
  });
}
